import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useQuery } from "convex/react";
import { api } from "@packages/backend/convex/_generated/api";
import { Id } from "@packages/backend/convex/_generated/dataModel";
import {
  Users,
  Calendar,
  MapPin,
  Target,
  BarChart as ChartBar,
  Briefcase,
  GraduationCap,
  Globe,
  ArrowUpRight,
  Mail,
  Search,
  Heart,
  Code,
} from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "../components/Avatar";
import { Badge } from "../components/Badge";
import { Input } from "../components/Input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/Dialog";
import { ScrollArea } from "../components/ScrollArea";

// Interfaces
interface CohortCandidate {
  id: string;
  name: string;
  age: number;
  currentStatus:
    | "Recent Graduate"
    | "University Student (Final Year)"
    | "University Student"
    | "Career Changer/Professional"
    | "Graduate changing profession"
    | "high school graduate";
  skills: string[];
  bio: string;
  trainingProject: string;
  interests: string;
  image: string;
  /** Placement status for Rwanda Training 2025 cohort */
  placementStatus?: "Available" | "Placed";
}

interface Cohort {
  name: string;
  description?: string;
  candidates: CohortCandidate[];
}

interface BPOProject {
  id: string;
  title: string;
  category: string;
  location: string;
  startDate: string;
  duration: string;
  participants: string;
  budget: string;
  image: string;
  description: string;
  bannerUrl?: string; // Convex field
  completedDate?: string; // Convex field
  clientName?: string; // Convex field
  problem?: {
    title: string;
    points: { title: string; description: string }[];
  };
  solution?: {
    title: string;
    description: string;
    roles: { jobs: string[]; positions: string[] };
  };
  timeline?: { date: string; milestone: string; description: string }[];
  candidateCriteria?: string[];
  targets?: { title: string; description: string }[];
  team?: { name: string; role: string; contact?: string; image: string }[];
  contact?: { email: string[]; phone: string; location: string };
  cohort?: Cohort;
}

// Data
const getProjectById = (id: string): BPOProject | null => {
  if (id !== "rwanda-training-2025") return null;

  return {
    id: "rwanda-training-2025",
    title: "MVP Data Analyst Skills Training & Job Placement",
    category: "Education & Training",
    location: "Rwanda",
    startDate: "February 2026",
    duration: "2 months",
    participants: "20 female participants",
    budget: "Co-Financed",
    image: "/images/bpo/data-processing.jpeg",
    description: `A pioneering German-Rwanda-Uganda collaboration between terratalent and Kolaborate, designed to bridge the gap between European companies' IT talent needs and Africa's skilled workforce potential.

terratalent, a boutique consulting company, specializes in sustainable recruitment from emerging countries, focusing on East and West Africa, Turkey, and India. Kolaborate Platforms, with a legal entity in Rwanda, connects businesses with top-tier African talent at affordable rates, empowering young professionals with global opportunities.`,
    problem: {
      title: "The Challenge We Address",
      points: [
        {
          title: "IT Talent Shortage",
          description:
            "IT talent is urgently needed in Germany and many parts of Europe. Digitisation projects are stalling.",
        },
        {
          title: "Untapped Workforce Potential",
          description:
            "Many young talents are unable to find suitable jobs. Youth unemployment is rising in Africa.",
        },
        {
          title: "Skills Matching",
          description:
            "The balance between supply and demand is complex. One issue is the matching of skills and competences.",
        },
      ],
    },
    solution: {
      title: "Our Solution",
      description:
        "A 6-month intensive training program for 20 female data analysts in Rwanda, designed to qualify young people for jobs with German or European Businesses through our BPO model.",
      roles: {
        jobs: [
          "BPO-Sector: Data Cleaning and Preparation",
          "IT-Outsourcing: Machine Learning and Data Science",
          "Data Analytics for Healthcare, Finance, Retail and Logistics Industries",
          "German and Rwandan Companies",
        ],
        positions: [
          "Data Analyst",
          "Data Scientist",
          "Machine Learning Engineer",
          "Business Intelligence Analyst",
          "Data Architect",
          "Business Systems Analyst",
        ],
      },
    },
    timeline: [
      {
        date: "Phase 1",
        milestone: "Candidate Recruitment and Assessment",
        description:
          "Interviews, skills tests, and personality assessments on the ground. Starting with group sessions explaining the program followed by 1:1 interviews.",
      },
      {
        date: "Phase 2",
        milestone: "Start 5-Month Training",
        description:
          "Full-time training combining classroom lessons and online exercises, focusing on essential skills for data analysis and project contribution.",
      },
      {
        date: "Phase 3",
        milestone: "Internship Phase",
        description:
          "Supporting graduates with placement in local, German, or European BPO and data-driven companies for remote work internships.",
      },
      {
        date: "Phase 4",
        milestone: "Start Placement Phase",
        description:
          "Focus on securing remote jobs with African and international companies, particularly BPO customers from Germany and Europe.",
      },
    ],
    candidateCriteria: [
      "No higher learning education requirement",
      "Open for university or high school dropouts",
      "Background in math, statistics, economics, computer science, or another quantitative field",
      "Basic computer skills",
      "Good proficiency in English",
      "Open to work in an international environment",
    ],
    targets: [
      {
        title: "Target 01",
        description:
          "Attracting IT & machine learning companies to collaborate with African talent.",
      },
      {
        title: "Target 02",
        description: "Creating jobs on the ground to foster gainful employment",
      },
      {
        title: "Target 03",
        description:
          "Targeting young talent from underprivileged areas, especially women.",
      },
    ],
    team: [
      {
        name: "Pearl Gakazi",
        role: "CO-Founder Kolaborate",
        contact: "support@kolaborate.co",
        image: "/images/bpo/team/pearl.png",
      },
      {
        name: "Burkhard Volbracht",
        role: "Co-Founder terratalent",
        contact: "burkhard.volbracht@terratalents.de",
        image: "/images/bpo/team/burkhard.png",
      },
      {
        name: "Martin Theobald",
        role: "Owner terratalent",
        image: "/images/bpo/team/martin.png",
      },
    ],
    contact: {
      email: ["burkhard.volbracht@terratalents.de", "support@kolaborate.co"],
      phone: "+256 772 957990 (Pearl Gakazi)",
      location: "Hauptstr. 27, 10825 Berlin",
    },
    cohort: {
      name: "Data Analytics Cohort",
      description:
        "Our first cohort of 19 talented women trained in data analytics, ready for internship placements.",
      candidates: [
        {
          id: "ajeneza-alliance",
          name: "Ajeneza Alliance",
          age: 23,
          currentStatus: "Recent Graduate",
          skills: ["Microsoft Excel (Advanced)", "Power BI / Tableau", "SQL"],
          bio: "My career goal is to grow as a skilled data analyst who can turn complex data into meaningful insights that support business decisions.",
          trainingProject: "Game sales data analysis Project.",
          interests:
            "Reading about Ai , Learning new data and tech skills,fitness and nature walks",
          image: "/cohort/ajeneza.png",
          placementStatus: "Placed",
        },
        {
          id: "clarisse-tuyishimire",
          name: "Clarisse Tuyishimire",
          age: 25,
          currentStatus: "Recent Graduate",
          skills: [
            "Microsoft Excel (Advanced)",
            "Power BI / Tableau",
            "Data Cleaning",
          ],
          bio: "My goal is to build a strong career in data analytics, using data to guide better decisions. I am focused on growing my technical skills and contributing to impactful, data-driven projects.",
          trainingProject:
            "A tomato leaf disease classification project using a Convolutional Neural Network (CNN). The dataset included 10,000 labeled training images and 1,000 test/validation images of tomato leaves with different diseases. I preprocessed the images by resizing and normalizing them, then split the data into training and validation sets. Finally, I trained a CNN model to learn image features and classify the leaves into their correct disease categories.",
          interests:
            "I enjoy learning new tech skills, watching educational content, and exploring creative activities",
          image: "/cohort/clarisse.png",
          placementStatus: "Placed",
        },
        {
          id: "divine-kaliza",
          name: "Divine Kaliza",
          age: 20,
          currentStatus: "University Student (Final Year)",
          skills: ["Power BI / Tableau", "SQL", "Data Cleaning"],
          bio: "My career goal is to grow in a way that I can continue learning and contribute meaningfully. I want to use my skills to it's full potential where I can solve different real problems and help others.",
          trainingProject:
            "BANK CUSTOMERS ANALYSIS. This is a project that we worked on using Power BI, where we were given different information on customers so as to create story telling dashboards.",
          interests: "I enjoy reading very much, swimming and playing tennis",
          image: "/cohort/divine-kaliza.png",
          placementStatus: "Placed",
        },
        {
          id: "divine-sugi-bigirimana",
          name: "Divine SUGI BIGIRIMANA",
          age: 24,
          currentStatus: "University Student (Final Year)",
          skills: [
            "Microsoft Excel (Advanced)",
            "Power BI / Tableau",
            "Data Cleaning",
          ],
          bio: "My career goal is to master and build strong expertise in data analytics, digital marketing, and hospitality so I can create innovative solutions that drive business growth. I also aim to use these skills to build and manage my own successful businesses in the future.",
          trainingProject:
            "Game sales data analysis and Disease prediction machine learning project",
          interests:
            "In my free time, I enjoy reading novels and exploring opportunities online, including job searching and learning new things. I also like relaxing by scrolling through social media platforms or getting some rest.",
          image: "/cohort/divine-sugi.png",
          placementStatus: "Placed",
        },
        {
          id: "gisele-usanawe",
          name: "Gisele Usanawe",
          age: 24,
          currentStatus: "Recent Graduate",
          skills: ["SQL", "Python / R", "Data Cleaning"],
          bio: "My career goal is to become a data scientist, using data analysis and machine learning to uncover insights and solve complex problems. I aim to contribute to projects that turn data into meaningful, actionable solutions.",
          trainingProject:
            "A capstone project which involved multiple data-driven tasks, including analyzing and visualizing bank customer, account, loan, and transaction data using Power BI, building machine learning models to predict diseases from structured datasets and developing a Convolutional Neural Network (CNN) to classify tomato leaf images into various disease categories through preprocessing, training and validation.",
          interests: "Reading, playing puzzle games and taking walk",
          image: "/cohort/gisele.png",
          placementStatus: "Available",
        },
        {
          id: "honette-ayingeneye",
          name: "Honette AYINGENEYE",
          age: 24,
          currentStatus: "Recent Graduate",
          skills: [
            "Microsoft Excel (Advanced)",
            "Power BI / Tableau",
            "Data Cleaning",
          ],
          bio: "I want to establish myself as a data analyst so that I can contribute to organizations by providing accurate insights that guide strategic decisions.",
          trainingProject:
            "A capstone project where I used Excel, python and power bi to clean data, analyze the trends and create a dashboard to visualize the findings.",
          interests: "Playing games and reading articles",
          image: "/cohort/honette-ayingeneye.png",
          placementStatus: "Placed",
        },
        {
          id: "irene-mushimire",
          name: "Irene Mushimire",
          age: 27,
          currentStatus: "Recent Graduate",
          skills: [
            "Microsoft Excel (Advanced)",
            "Power BI / Tableau",
            "Data Cleaning",
          ],
          bio: "My career goal is to become a data analyst who transforms complex information into insights that drive smarter decisions. I aim to deepen my skills in analytics and visualization so I can help organizations solve meaningful problems with data.",
          trainingProject: "Bank customer system of Power BI",
          interests:
            "In my free time I like to read books, sing and exercise my body.",
          image: "/cohort/irene.png",
          placementStatus: "Placed",
        },
        {
          id: "ishimwe-mwambutsa-daniella",
          name: "Ishimwe Mwambutsa Daniella",
          age: 23,
          currentStatus: "Recent Graduate",
          skills: ["Microsoft Excel (Advanced)", "Python / R", "Data Cleaning"],
          bio: "My career goal is to become a data analyst, using data analysis to provide valuable insights and drive informed decision-making. I am dedicated to transforming data into actionable strategies.",
          trainingProject: "Disease prediction machine learning project",
          interests:
            "Listening to music, Watching comedy show, Going out with my friends & Family",
          image: "/cohort/ishimwe.png",
          placementStatus: "Available",
        },
        {
          id: "kabayiza-ishimwe-ines",
          name: "Kabayiza Ishimwe Ines",
          age: 24,
          currentStatus: "Recent Graduate",
          skills: ["Microsoft Excel (Advanced)", "Data Cleaning"],
          bio: "My goal is to grow in the business field by gaining practical experience and developing skills that support organizational decision-making. I am open to learning from different roles so I can better understand how businesses operate.",
          trainingProject:
            "Basic Sales Data Analysis and Data cleaning using Excel",
          interests:
            "I enjoy creative writing, including poetry, essays, and stories. I also love reading and exploring new places or ideas in my free time.",
          image: "/cohort/kabayiza.png",
          placementStatus: "Placed",
        },
        {
          id: "kevine-simbi-bigirimana",
          name: "Kevine SIMBI BIGIRIMANA",
          age: 24,
          currentStatus: "Recent Graduate",
          skills: [
            "Microsoft Excel (Advanced)",
            "Power BI / Tableau",
            "Data Cleaning",
          ],
          bio: "My career goal is to become an exceptional data analyst who applies strong analytical skills to solve real-world business and economic problems. I aim to blend data analytics with my background in applied statistics in economics to drive smarter decisions and impactful insights.",
          trainingProject:
            "Tomato Leaf disease detection (CNN Learning Project) where the project involved collecting and preprocessing images, training a convolutional neural network, and evaluating its accuracy to support early detection and improve crop health management.",
          interests: "Reading novels, Working out and Cooking.",
          image: "/cohort/kevine-simbi.png",
          placementStatus: "Available",
        },
        {
          id: "monique-uwase",
          name: "Monique UWASE",
          age: 25,
          currentStatus: "Recent Graduate",
          skills: [
            "Microsoft Excel (Advanced)",
            "Power BI / Tableau",
            "Python / R",
          ],
          bio: "My career goal is to become a skilled data analyst who transforms raw data into meaningful insights that support smarter, AI-driven decision-making. I aim to apply analytical tools, machine learning, and AI techniques to solve real-world problems and help organizations make strategic, data-informed choices.",
          trainingProject:
            "An interactive Power BI dashboard that analyzes bank customer data, including customer profiles, account details, deposits, loans, and transaction patterns. The project involved data cleaning, creating DAX measures, building slicers, and designing four dashboards: Customer Profile, Accounts & Deposits, Loan Insights, and Transactions. The dashboard helps visualize key trends and support data-driven decision-making.",
          interests:
            "I enjoy listening to music, watching movies, and going to the gym and engaging in sports activities.",
          image: "/cohort/monique.png",
          placementStatus: "Placed",
        },
        {
          id: "niyigena-fifi",
          name: "Niyigena Fifi",
          age: 21,
          currentStatus: "high school graduate",
          skills: [
            "Microsoft Excel (Advanced)",
            "Power BI / Tableau",
            "Data Cleaning",
          ],
          bio: "My goal is to build a career in data analysis,turning raw data into meaningful. I am eager to use my skills in data visualisation and problem_solving to support informed decision -making",
          trainingProject: "Disease prediction (machine learning )",
          interests:
            "I enjoy learning new things ,l also like reading and practicing English to improve my communication , l like to read books and watching movie",
          image: "/cohort/niyigena.png",
          placementStatus: "Placed",
        },
        {
          id: "raisa-mbabazi",
          name: "Raisa MBABAZI",
          age: 28,
          currentStatus: "Career Changer/Professional",
          skills: ["Microsoft Excel (Advanced)", "Power Bi", "Python"],
          bio: "I want to build a career in Data Analytics and help businesses make better decisions using data. I’m committed to learning more every day and growing into a confident, reliable data professional.",
          trainingProject:
            "A data analytics project where I cleaned and explored a sales dataset using Excel and SQL, then used Python to analyze the data and show the results in a clear way.",
          interests:
            "I enjoy cooking, learning new skills, and spending time with family.",
          image: "/cohort/raisa-mbabazi.png",
          placementStatus: "Available",
        },
        {
          id: "roseline-niyigaba",
          name: "Roseline Niyigaba",
          age: 23,
          currentStatus: "Recent Graduate",
          skills: [
            "Microsoft Excel (Advanced)",
            "Power BI / Tableau",
            "Python / R",
          ],
          bio: "I want to be a policy maker in this era of digital transformation, ensuring that the solutions put out there are human centered, unbiased, and contexturized to the needs and culture of the African community. Working with local policy makers, and organization to make sure that our community doesn't import foreign tech solutions such as AI tech but a hub for digital transformation.",
          trainingProject: "Getting insights from a bank data",
          interests: "Listening to music, watching movies and culinary classes",
          image: "/cohort/roseline.png",
          placementStatus: "Placed",
        },
        {
          id: "sandrine-umutoniwase",
          name: "Sandrine Umutoniwase",
          age: 26,
          currentStatus: "Recent Graduate",
          skills: ["Microsoft Excel (Advanced)", "Power BI / Tableau", "SQL"],
          bio: "My goal is to become a data analytics professional who transforms raw data into clear insights that support smart decision-making. I aim to develop strong skills in analysis, visualization, and reporting to help organizations solve real problems and improve performance.",
          trainingProject:
            "One project I worked on during training involved analyzing a dataset using Python for cleaning and transformation, then creating interactive dashboards in Power BI to present clear insights for decision-making.",
          interests:
            "I enjoy exploring new tech tools and learning data-related skills. I also like relaxing with music or spending time with friends and family.",
          image: "/cohort/sandrine.png",
          placementStatus: "Available",
        },
        {
          id: "theodosie-uwayezu-bwiza",
          name: "Theodosie UWAYEZU BWIZA",
          age: 25,
          currentStatus: "Recent Graduate",
          skills: ["Power BI / Tableau", "SQL", "Python / R"],
          bio: "My career goal is to become an expert data analyst, using my data analysis skills to help companies make better decisions. I also want to apply my skills in Python and machine learning for prediction to solve real world business problems.",
          trainingProject:
            "A project called Sales Performance Analysis, where I cleaned and analyzed the data, created visual dashboards, and used Python to build machine learning models to predict future sales.",
          interests:
            "1.Playing the violin 2. Doing research 3.Reading books, especially in German",
          image: "/cohort/theodosie.png",
          placementStatus: "Placed",
        },
        {
          id: "tumukunde-anastasie",
          name: "TUMUKUNDE Anastasie",
          age: 24,
          currentStatus: "Recent Graduate",
          skills: ["Microsoft Excel (Advanced)", "Python / R", "Data Cleaning"],
          bio: "My career goal is to become a skilled data analyst who uses statistical and analytical tools to solve real world problems and support evidence based decision making. I aim to specialize in areas like demography and public health, using data to create meaningful impact in my community and beyond.",
          trainingProject:
            "One project I worked on during training was building a Convolutional Neural Network (CNN) model to classify tomato leaf diseases using an image dataset. The model was trained to detect multiple diseases such as Early Blight, Late Blight, Target Spot, and Tomato Mosaic Virus.",
          interests: "Playing volleyball, traveling and doing a business",
          image: "/cohort/TUMUKUNDE.png",
          placementStatus: "Placed",
        },
        {
          id: "zaneza-ellora-sapna",
          name: "Zaneza ellora sapna",
          age: 21,
          currentStatus: "high school graduate",
          skills: ["Microsoft Excel (Advanced)", "SQL", "Python / R"],
          bio: "My career goal is to become a skilled Data Analyst who uses data to improve business decisions and customer experiences. I aim to grow in a modern, technology-driven work environment where I can keep learning and contributing.",
          trainingProject:
            "During my training, I worked on 1.Game sales data analysis 2.Disease prediction machine learning project 3.Tomato Leaf Disease detection (CNN learning project) where I used Power BI,SQL and machine learning techniques to visualize data, identify patterns, and present insights. These projects gave me hands-on experience with real data and practical analytical skills.",
          interests:
            "Traveling & discovering new cultures, Creative work like designing or organizing events ,",
          image: "/cohort/Zaneza.png",
          placementStatus: "Placed",
        },
        {
          id: "emily-mudenge",
          name: "Emily Mudenge",
          age: 23,
          currentStatus: "University Student",
          skills: ["Microsoft Excel (Advanced)", "Power BI", "Data Cleaning"],
          bio: "My career goal is to grow into a data-science professional who is able to understand patterns, solve real problems, and make better decisions. I want to build strong skills in analytics and visualization so I can turn data into clear, useful insights for any organization I work with.",
          trainingProject:
            "Capstone project. One of the things I had to do was clean and present data visually using Power BI. This basically included finding the relationship among a lot of data, organizing them and presenting them in a way that would be both story telling and highlighting the most important details of the data.",
          interests: "Swimming, Working out, Travelling",
          image: "/cohort/emily.png",
          placementStatus: "Placed",
        },
      ],
    },
  };
};

const fadeInUp = {
  initial: { opacity: 0, y: 60 },
  animate: { opacity: 1, y: 0 },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const pulseAnimation = {
  initial: { scale: 1 },
  animate: {
    scale: [1, 1.02, 1],
  },
};

export const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  // Check if this is the hardcoded project (not a Convex ID)
  const isHardcodedProject = id === "rwanda-training-2025";

  // Only try to fetch from Convex if it's NOT the hardcoded project
  // and the ID looks like a valid Convex ID (starts with 'k' and is long enough)
  const convexProject = useQuery(
    api.marketplace.jobs.completedProjects.getById,
    !isHardcodedProject && id && id.startsWith("k") && id.length >= 20
      ? { id: id as Id<"completedProjects"> }
      : "skip",
  );

  const [selectedCandidate, setSelectedCandidate] =
    useState<CohortCandidate | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Use hardcoded data for the special project, otherwise use Convex data
  const project = isHardcodedProject
    ? getProjectById(id)
    : (convexProject as unknown as BPOProject);

  const isLoading = !isHardcodedProject && convexProject === undefined;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-secondary via-white to-brand-secondary">
        <div className="text-center p-8">
          <h1 className="text-4xl font-bold text-brand-grey mb-4 animate-pulse">
            Loading Project...
          </h1>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-secondary via-white to-brand-secondary">
        <div className="text-center p-8">
          <h1 className="text-4xl font-bold text-brand-grey mb-4">
            Project Not Found
          </h1>
          <p className="text-brand-grey/70 mb-6">
            The project you're looking for doesn't exist or has been moved.
          </p>
          <a
            href="/portfolio"
            className="inline-flex items-center gap-2 px-6 py-3 bg-brand-primary text-white rounded-full hover:bg-brand-primary/90 transition-colors"
          >
            View All Projects
          </a>
        </div>
      </div>
    );
  }

  const filteredCandidates = project.cohort?.candidates.filter(
    (candidate: CohortCandidate) => {
      const search = searchTerm.toLowerCase();
      return (
        candidate.name.toLowerCase().includes(search) ||
        candidate.skills.some((skill: string) =>
          skill.toLowerCase().includes(search),
        ) ||
        candidate.bio.toLowerCase().includes(search) ||
        candidate.currentStatus.toLowerCase().includes(search)
      );
    },
  );

  const getStatusColor = (status: string) => {
    return status
      ? "bg-brand-primary text-white"
      : "bg-brand-primary text-white";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-secondary via-white to-brand-secondary">
      <div className="relative">
        <div className="h-[40rem] overflow-hidden relative">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/45 to-black/70 z-10" />
          <img
            src={
              project.image ||
              project.bannerUrl ||
              "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1770&q=80"
            }
            alt={project.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 flex items-center z-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div
                initial="initial"
                animate="animate"
                variants={staggerContainer}
                className="space-y-6"
              >
                <motion.span
                  variants={fadeInUp}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  className="inline-block px-4 py-2 text-sm bg-brand-primary text-white rounded-full mb-4 shadow-lg"
                >
                  {project.category}
                </motion.span>
                <motion.h1
                  variants={fadeInUp}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  className="text-5xl md:text-7xl font-bold mb-6 text-white font-sans"
                >
                  {project.title}
                </motion.h1>
                <motion.div
                  variants={fadeInUp}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  className="grid grid-cols-2 md:grid-cols-4 gap-6 bg-white/10 backdrop-blur-md p-6 rounded-2xl"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-brand-primary rounded-full flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-white/60 text-sm">Location</p>
                      <p className="text-white font-medium">
                        {project.location || "Global"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-brand-primary rounded-full flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-white/60 text-sm">Completed</p>
                      <p className="text-white font-medium">
                        {project.startDate ||
                          project.completedDate ||
                          "Ongoing"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-brand-primary rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-white/60 text-sm">Participants</p>
                      <p className="text-white font-medium">
                        {project.participants || "TBD"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-brand-primary rounded-full flex items-center justify-center">
                      <Briefcase className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-white/60 text-sm">Client</p>
                      <p className="text-white font-medium">
                        {project.budget || project.clientName || "Confidential"}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 space-y-32">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
          <div className="lg:col-span-2 space-y-16">
            <motion.section
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="prose prose-lg max-w-none bg-white p-8 rounded-2xl shadow-lg border border-brand-secondary"
            >
              <h2 className="text-4xl font-sans font-bold text-brand-grey mb-6 flex items-center gap-3">
                <span className="w-12 h-12 bg-brand-primary rounded-full flex items-center justify-center flex-shrink-0">
                  <Globe className="w-6 h-6 text-white" />
                </span>
                Project Overview
              </h2>
              <p className="text-brand-grey/80 whitespace-pre-line leading-relaxed">
                {project.description}
              </p>
            </motion.section>

            {project.problem && project.problem.points && (
              <motion.section
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="space-y-6"
              >
                <h2 className="text-4xl font-sans font-bold text-brand-grey mb-8 flex items-center gap-3">
                  <span className="w-12 h-12 bg-brand-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <Target className="w-6 h-6 text-white" />
                  </span>
                  {project.problem.title}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {project.problem.points.map(
                    (
                      point: { title: string; description: string },
                      index: number,
                    ) => (
                      <motion.div
                        key={index}
                        variants={pulseAnimation}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeInOut",
                        }}
                        initial="initial"
                        whileInView="animate"
                        viewport={{ once: true }}
                        className="bg-white p-8 rounded-2xl shadow-lg border-2 border-brand-secondary hover:border-brand-primary transition-colors duration-300"
                      >
                        <h3 className="font-bold text-brand-grey mb-3 text-xl">
                          {point.title}
                        </h3>
                        <p className="text-brand-grey/80">
                          {point.description}
                        </p>
                      </motion.div>
                    ),
                  )}
                </div>
              </motion.section>
            )}

            {project.solution && (
              <motion.section
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="space-y-6"
              >
                <h2 className="text-4xl font-sans font-bold text-brand-grey mb-8 flex items-center gap-3">
                  <span className="w-12 h-12 bg-brand-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <Globe className="w-6 h-6 text-white" />
                  </span>
                  {project.solution.title}
                </h2>
                <p className="text-brand-grey/80">
                  {project.solution.description}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="bg-white p-6 rounded-xl shadow-sm">
                    <h3 className="font-bold text-brand-grey mb-4">
                      Target Jobs
                    </h3>
                    <ul className="space-y-2">
                      {project.solution.roles.jobs.map(
                        (job: string, index: number) => (
                          <li key={index} className="flex items-start gap-2">
                            <ChartBar className="w-5 h-5 text-brand-primary flex-shrink-0 mt-1" />
                            <span className="text-brand-grey/80">{job}</span>
                          </li>
                        ),
                      )}
                    </ul>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-sm">
                    <h3 className="font-bold text-brand-grey mb-4">
                      Target Positions
                    </h3>
                    <ul className="space-y-2">
                      {project.solution.roles.positions.map(
                        (position: string, index: number) => (
                          <li key={index} className="flex items-start gap-2">
                            <Briefcase className="w-5 h-5 text-brand-primary flex-shrink-0 mt-1" />
                            <span className="text-brand-grey/80">
                              {position}
                            </span>
                          </li>
                        ),
                      )}
                    </ul>
                  </div>
                </div>
              </motion.section>
            )}
          </div>

          <div className="space-y-8 sticky top-24 self-start">
            {project.candidateCriteria && (
              <motion.section
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="bg-white p-8 rounded-2xl shadow-lg border border-brand-secondary hover:border-brand-primary transition-colors duration-300"
              >
                <h3 className="text-2xl font-sans font-bold text-brand-grey mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-brand-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <GraduationCap className="w-5 h-5 text-white" />
                  </span>
                  Candidate Criteria
                </h3>
                <div className="space-y-4">
                  {project.candidateCriteria.map(
                    (criterion: string, index: number) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                        className="flex items-start gap-3 p-4 rounded-xl bg-brand-secondary/20 hover:bg-brand-secondary/30 transition-colors"
                      >
                        <GraduationCap className="w-5 h-5 text-brand-primary flex-shrink-0 mt-1" />
                        <p className="text-brand-grey/80">{criterion}</p>
                      </motion.div>
                    ),
                  )}
                </div>
              </motion.section>
            )}

            {project.team && (
              <motion.section
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="bg-white p-8 rounded-2xl shadow-lg border border-brand-secondary hover:border-brand-primary transition-colors duration-300"
              >
                <h3 className="text-2xl font-sans font-bold text-brand-grey mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-brand-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-white" />
                  </span>
                  Meet the Team
                </h3>
                <div className="space-y-6">
                  {project.team.map(
                    (
                      member: {
                        name: string;
                        role: string;
                        contact?: string;
                        image: string;
                      },
                      index: number,
                    ) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                        className="flex items-start gap-4 p-4 rounded-xl bg-brand-secondary/10 hover:bg-brand-secondary/20 transition-all duration-300 group"
                      >
                        <Avatar className="h-20 w-20 flex-shrink-0 border-2 border-brand-primary/20 group-hover:border-brand-primary transition-colors">
                          <AvatarImage src={member.image} alt={member.name} />
                          <AvatarFallback>
                            {member.name
                              .split(" ")
                              .map((n: string) => n[0])
                              .join("")}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-grow min-w-0 overflow-hidden">
                          <h4 className="font-medium text-brand-grey text-lg truncate">
                            {member.name}
                          </h4>
                          <p className="text-sm text-brand-grey/60 truncate">
                            {member.role}
                          </p>
                          {member.contact && (
                            <div className="flex items-center gap-1 mt-1">
                              <Mail className="w-4 h-4 flex-shrink-0 text-brand-primary" />
                              <a
                                href={`mailto:${member.contact}`}
                                className="text-sm text-brand-primary hover:text-brand-tertiary transition-colors truncate"
                              >
                                {member.contact}
                              </a>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    ),
                  )}
                </div>
              </motion.section>
            )}
          </div>
        </div>

        {/* Full-width Timeline Section */}
        {project.timeline && (
          <motion.section
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-12"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-4xl font-sans font-bold text-brand-grey flex items-center gap-3">
                <span className="w-12 h-12 bg-brand-primary rounded-full flex items-center justify-center flex-shrink-0 shadow-lg shadow-brand-primary/20">
                  <Calendar className="w-6 h-6 text-white" />
                </span>
                Project Timeline
              </h2>
              <div className="hidden md:block h-px flex-grow mx-8 bg-gradient-to-r from-brand-primary/20 to-transparent" />
            </div>

            <div className="relative pl-8 md:pl-0">
              {/* Vertical Line */}
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-brand-primary via-brand-tertiary to-brand-secondary/30 rounded-full transform md:-translate-x-1/2" />

              <div className="space-y-12">
                {project.timeline.map(
                  (
                    item: {
                      date: string;
                      milestone: string;
                      description: string;
                    },
                    index: number,
                  ) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true, margin: "-100px" }}
                      transition={{ duration: 0.7, delay: index * 0.1 }}
                      className={`relative flex flex-col md:flex-row items-center gap-8 ${
                        index % 2 !== 0 ? "md:flex-row-reverse" : ""
                      }`}
                    >
                      {/* Timeline Dot */}
                      <div className="absolute left-[-28px] md:left-1/2 w-6 h-6 bg-white border-4 border-brand-primary rounded-full z-10 transform md:-translate-x-1/2 shadow-xl" />

                      {/* Content Card */}
                      <div className="w-full md:w-[45%] group">
                        <div className="bg-white p-8 rounded-3xl shadow-[0_10px_40px_rgba(0,0,0,0.04)] border border-brand-secondary hover:border-brand-primary/30 transition-all duration-500 hover:shadow-[0_20px_60px_rgba(0,0,0,0.08)] hover:-translate-y-2 relative overflow-hidden">
                          {/* Phase Badge */}
                          <div
                            className={`absolute top-0 ${index % 2 !== 0 ? "right-0 rounded-bl-2xl" : "left-0 rounded-br-2xl"} bg-brand-primary/10 px-4 py-2 text-brand-primary font-bold text-sm tracking-wider uppercase`}
                          >
                            {item.date}
                          </div>

                          <div className="pt-4">
                            <h3 className="text-2xl font-bold text-brand-grey mb-4 group-hover:text-brand-primary transition-colors">
                              {item.milestone}
                            </h3>
                            <p className="text-brand-grey/70 leading-relaxed text-lg">
                              {item.description}
                            </p>
                          </div>

                          {/* Decorative Elements */}
                          <div
                            className={`absolute bottom-0 ${index % 2 !== 0 ? "left-0" : "right-0"} w-24 h-24 bg-brand-primary/5 rounded-full -m-12 group-hover:scale-150 transition-transform duration-700`}
                          />
                        </div>
                      </div>

                      {/* Spacer for large screens */}
                      <div className="hidden md:block w-[45%]" />
                    </motion.div>
                  ),
                )}
              </div>
            </div>
          </motion.section>
        )}

        {/* Cohort Section - Full Width */}
        {project.cohort && (
          <motion.section
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-6 mt-24"
          >
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="space-y-2">
                <h2 className="text-4xl font-sans font-bold text-brand-grey flex items-center gap-3">
                  <span className="w-12 h-12 bg-brand-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-white" />
                  </span>
                  {project.cohort.name}
                </h2>
                {project.cohort.description && (
                  <p className="text-brand-grey/80 max-w-2xl">
                    {project.cohort.description}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-4">
                <div className="relative w-full md:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-grey/50" />
                  <Input
                    type="text"
                    placeholder="Search candidates..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 bg-white border-brand-secondary"
                  />
                </div>
                <Badge
                  variant="secondary"
                  className="hidden md:flex h-10 px-4 text-sm items-center justify-center whitespace-nowrap"
                >
                  {filteredCandidates?.length} Candidates
                </Badge>
              </div>
            </div>

            {filteredCandidates && filteredCandidates.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredCandidates.map((candidate, index) => (
                  <motion.div
                    key={candidate.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    whileHover={{ y: -5 }}
                    onClick={() => setSelectedCandidate(candidate)}
                    className="bg-white p-6 rounded-xl shadow-lg border border-brand-secondary hover:border-brand-primary transition-all duration-300 cursor-pointer group"
                  >
                    <div className="flex items-start gap-4">
                      <Avatar className="h-16 w-16 flex-shrink-0 border-2 border-brand-primary/20 group-hover:border-brand-primary transition-colors">
                        <AvatarImage
                          src={candidate.image}
                          alt={candidate.name}
                        />
                        <AvatarFallback className="bg-brand-primary/10 text-brand-primary font-medium">
                          {candidate.name
                            .split(" ")
                            .map((n) => n[0])
                            .join("")
                            .slice(0, 2)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-grow min-w-0">
                        <h4 className="font-semibold text-brand-grey text-lg truncate group-hover:text-brand-primary transition-colors">
                          {candidate.name}
                        </h4>
                        <p className="text-sm text-brand-grey font-medium">
                          Age: {candidate.age}
                        </p>
                        <Badge
                          className={`mt-1 text-xs ${getStatusColor(candidate.currentStatus)}`}
                        >
                          {candidate.currentStatus}
                        </Badge>
                        {candidate.placementStatus && (
                          <Badge
                            variant={
                              candidate.placementStatus === "Available"
                                ? "default"
                                : "secondary"
                            }
                            className={`mt-1 ml-1 text-xs ${
                              candidate.placementStatus === "Available"
                                ? "bg-green-600 text-white hover:bg-green-700"
                                : "bg-brand-grey/20 text-brand-grey"
                            }`}
                          >
                            {candidate.placementStatus}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-1">
                      {candidate.skills.slice(0, 3).map((skill, skillIndex) => (
                        <Badge
                          key={skillIndex}
                          variant="outline"
                          className="text-xs border-brand-primary/30 text-brand-grey/70"
                        >
                          {skill}
                        </Badge>
                      ))}
                    </div>
                    <p className="mt-3 text-sm text-brand-grey leading-relaxed line-clamp-2">
                      {candidate.bio}
                    </p>
                    <div className="mt-3 flex items-center text-brand-primary text-sm font-medium group-hover:translate-x-1 transition-transform">
                      View Profile <ArrowUpRight className="w-4 h-4 ml-1" />
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-xl border border-brand-secondary">
                <Users className="w-12 h-12 text-brand-grey/30 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-brand-grey">
                  No candidates found
                </h3>
                <p className="text-brand-grey/60">
                  Try adjusting your search terms to find what you&apos;re
                  looking for.
                </p>
              </div>
            )}
          </motion.section>
        )}
      </div>

      {/* Candidate Profile Modal */}
      <Dialog
        open={!!selectedCandidate}
        onOpenChange={() => setSelectedCandidate(null)}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] p-0 overflow-hidden">
          {selectedCandidate && (
            <>
              <DialogHeader className="p-6 pb-0">
                <div className="flex items-start gap-4">
                  <Avatar className="h-20 w-20 flex-shrink-0 border-2 border-brand-primary">
                    <AvatarImage
                      src={selectedCandidate.image}
                      alt={selectedCandidate.name}
                    />
                    <AvatarFallback className="bg-brand-primary/10 text-brand-primary text-xl font-medium">
                      {selectedCandidate.name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")
                        .slice(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-grow">
                    <DialogTitle className="text-2xl font-sans text-brand-grey">
                      {selectedCandidate.name}
                    </DialogTitle>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <Badge
                        className={getStatusColor(
                          selectedCandidate.currentStatus,
                        )}
                      >
                        {selectedCandidate.currentStatus}
                      </Badge>
                      {selectedCandidate.placementStatus && (
                        <Badge
                          variant={
                            selectedCandidate.placementStatus === "Available"
                              ? "default"
                              : "secondary"
                          }
                          className={
                            selectedCandidate.placementStatus === "Available"
                              ? "bg-green-600 text-white hover:bg-green-700"
                              : "bg-brand-grey/20 text-brand-grey"
                          }
                        >
                          {selectedCandidate.placementStatus}
                        </Badge>
                      )}
                      <span className="text-brand-grey/60 text-sm">
                        Age: {selectedCandidate.age}
                      </span>
                    </div>
                  </div>
                </div>
              </DialogHeader>
              <ScrollArea className="max-h-[60vh] p-6 pt-4">
                <div className="space-y-6">
                  {/* Bio */}
                  <div>
                    <h4 className="font-semibold text-brand-grey flex items-center gap-2 mb-2">
                      <Briefcase className="w-4 h-4 text-brand-primary" />
                      Professional Bio
                    </h4>
                    <p className="text-brand-grey/80 text-sm leading-relaxed">
                      {selectedCandidate.bio}
                    </p>
                  </div>

                  {/* Skills */}
                  <div>
                    <h4 className="font-semibold text-brand-grey flex items-center gap-2 mb-2">
                      <Code className="w-4 h-4 text-brand-primary" />
                      Technical Skills
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedCandidate.skills.map((skill, index) => (
                        <Badge
                          key={index}
                          variant="outline"
                          className="border-brand-primary text-brand-primary"
                        >
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Training Project */}
                  <div>
                    <h4 className="font-semibold text-brand-grey flex items-center gap-2 mb-2">
                      <GraduationCap className="w-4 h-4 text-brand-primary" />
                      Training Project
                    </h4>
                    <p className="text-brand-grey/80 text-sm leading-relaxed bg-brand-secondary/20 p-4 rounded-lg">
                      {selectedCandidate.trainingProject}
                    </p>
                  </div>

                  {/* Interests */}
                  <div>
                    <h4 className="font-semibold text-brand-grey flex items-center gap-2 mb-2">
                      <Heart className="w-4 h-4 text-brand-primary" />
                      Personal Interests
                    </h4>
                    <p className="text-brand-grey/80 text-sm leading-relaxed">
                      {selectedCandidate.interests}
                    </p>
                  </div>
                </div>
              </ScrollArea>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
